import React, { useState, useEffect, useRef } from 'react';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

const solutions = [
  { title: 'Automation', path: '/solutions/automation', description: 'Streamline operations with intelligent automation solutions' },
  { title: 'Artificial Intelligence', path: '/solutions/artificial-intelligence', description: 'Leverage AI for predictive analytics and insights' },
  { title: 'Data Engineering', path: '/solutions/data-engineering', description: 'Transform data into actionable insights' },
  { title: 'Web & Mobile Development', path: '/solutions/web-mobile-development', description: 'Custom applications built with modern frameworks' },
  { title: 'BI & Analytics', path: '/solutions/bi-analytics', description: 'Enterprise dashboards, automated reporting, and advanced analytics' },
  { title: 'Cloud Migration & Infrastructure', path: '/solutions/cloud-migration', description: 'Zero data loss migrations to modern cloud platforms' },
  { title: 'AI Audit & Assessment', path: '/solutions/ai-audit', description: 'Independent evidence-based assessment of AI systems and architectures' },
];

const services = [
  { title: 'AI Implementation Services', path: '/ai-implementation-services', description: 'Production-ready AI systems deployed in 3-8 weeks' },
  { title: 'Hire AI Developers', path: '/hire-ai-developers', description: 'Senior AI engineers and ML specialists' },
  { title: 'OpenAI GPT Development', path: '/openai-gpt-development', description: 'Custom GPT-4 applications and RAG implementation' },
  { title: 'AI Consulting USA', path: '/ai-consulting-company-usa', description: 'AI solutions for US enterprises' },
  { title: 'AI Consulting Ahmedabad', path: '/ai-consulting-company-ahmedabad', description: 'Local AI expertise in Gujarat, India' },
  { title: 'AI Consulting UK', path: '/ai-consulting-uk', description: 'Production-ready AI systems for UK enterprises' },
  { title: 'AI Chatbot Development', path: '/ai-chatbot-development', description: 'Custom chatbots deployed in 3-5 weeks' },
  { title: 'AI Agent Development', path: '/ai-agent-development', description: 'Autonomous agents that execute workflows' },
  { title: 'LLM Development', path: '/llm-development-services', description: 'Fine-tuning, RAG and private LLM deployment' },
  { title: 'Data Migration', path: '/data-migration-services', description: 'Zero-downtime cloud and database migration' },
  { title: 'Custom AI Development', path: '/custom-ai-development', description: 'Bespoke AI solutions built to your spec' },
  { title: 'FMCG AI', path: '/fmcg-ai', description: 'Cloud migration and AI for FMCG enterprises' },
  { title: 'Pharma Marketing Analytics', path: '/pharmaceutical-marketing-analytics', description: 'NPI validation and PLD compliance data platforms' },
  { title: 'Sports Analytics', path: '/sports-analytics', description: 'AI-powered sports prediction and performance systems' },
  { title: 'Maritime AI', path: '/maritime-ai', description: 'AI automation for ship chandlers and maritime distributors' },
  { title: 'Generative AI Consulting', path: '/generative-ai-consulting', description: 'RAG pipelines, AI agents, and LLM integration' },
];

const caseStudies = [
  { title: 'Retail Analytics', path: '/case-studies/retail-analytics', description: 'How we transformed operations for a leading UK pharmacy chain' },
  { title: 'Healthcare AI', path: '/case-studies/healthcare-ai', description: 'AI-driven cardiac risk prediction platform for improved patient care' },
  { title: 'Healthcare Communication', path: '/case-studies/healthcare-communication', description: 'Real-time clinical intelligence platform for patient safety' },
  { title: 'Insurance Automation', path: '/case-studies/insurance-automation', description: 'Automated statement processing for a leading insurance brokerage' },
  { title: 'Manufacturing AI', path: '/case-studies/manufacturing-ai', description: 'AI-driven demand forecasting for optimized inventory management' },
  { title: 'Payment Automation', path: '/case-studies/payment-automation', description: 'Streamlined payment processing for 1000+ delivery partners' },
];

const products = [
  { title: 'Tidal', url: 'https://tidal.supply/', description: 'AI-powered supply chain and procurement platform' },
  { title: 'OmniTrade', url: 'https://omnitrade24.com', description: 'Unified trading platform for modern markets' },
  { title: 'AutomationMart', url: 'https://automationmart.io/', description: 'Marketplace for business automation solutions' },
  { title: 'River', url: '/river', description: 'Production poker platforms for web, iOS, and Android' },
];

const industries = [
  { title: 'FMCG', path: '/industries/fmcg', description: 'Demand forecasting, trade promotion & supply chain AI' },
  { title: 'Global Trade', path: '/industries/global-trade', description: 'Trade document automation, compliance & freight AI' },
  { title: 'Trading & Investment', path: '/industries/trading-investment', description: 'Algorithmic trading, risk analytics & compliance AI' },
  { title: 'Maritime', path: '/industries/maritime', description: 'Vessel performance, procurement & port operations AI' },
  { title: 'Sports & Entertainment', path: '/industries/sports-entertainment', description: 'Performance analytics, fan engagement & media AI' },
  { title: 'Pharmaceutical Marketing', path: '/industries/pharmaceutical-marketing', description: 'HCP engagement, commercial analytics & content AI' },
];

interface MegaDropdownProps {
  items: { title: string; path?: string; url?: string; description: string }[];
  visible: boolean;
  ctaTitle: string;
  ctaText: string;
  ctaLink: string;
  ctaButtonLabel: string;
  viewAllLink?: string;
  viewAllLabel?: string;
  alignRight?: boolean;
}

const MegaDropdown: React.FC<MegaDropdownProps> = ({
  items, visible, ctaTitle, ctaText, ctaLink, ctaButtonLabel,
  viewAllLink, viewAllLabel, alignRight = false,
}) => {
  const half = Math.ceil(items.length / 2);
  const col1 = items.slice(0, half);
  const col2 = items.slice(half);

  return (
    <div
      className={`absolute ${alignRight ? 'right-0' : 'left-0'} top-full z-[200]`}
      style={{
        width: '620px',
        paddingTop: '8px',
        pointerEvents: visible ? 'auto' : 'none',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(-8px)',
        visibility: visible ? 'visible' : 'hidden',
        transition: 'opacity 180ms ease, transform 180ms ease, visibility 180ms',
      }}
    >
      <div className="bg-gray-900 rounded-lg shadow-2xl flex overflow-hidden">
        <div className="flex-1 grid grid-cols-2 gap-x-2 p-4">
          <div>
            {col1.map((item, i) =>
              item.url ? (
                <a
                  key={i}
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block px-3 py-2.5 rounded hover:bg-gray-800 group"
                >
                  <div className="text-sm font-semibold text-white group-hover:text-teal-400 transition-colors">{item.title}</div>
                  <div className="text-xs text-gray-400 mt-0.5 leading-snug">{item.description}</div>
                </a>
              ) : (
                <Link
                  key={i}
                  to={item.path!}
                  className="block px-3 py-2.5 rounded hover:bg-gray-800 group"
                >
                  <div className="text-sm font-semibold text-white group-hover:text-teal-400 transition-colors">{item.title}</div>
                  <div className="text-xs text-gray-400 mt-0.5 leading-snug">{item.description}</div>
                </Link>
              )
            )}
          </div>
          <div>
            {col2.map((item, i) =>
              item.url ? (
                <a
                  key={i}
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block px-3 py-2.5 rounded hover:bg-gray-800 group"
                >
                  <div className="text-sm font-semibold text-white group-hover:text-teal-400 transition-colors">{item.title}</div>
                  <div className="text-xs text-gray-400 mt-0.5 leading-snug">{item.description}</div>
                </a>
              ) : (
                <Link
                  key={i}
                  to={item.path!}
                  className="block px-3 py-2.5 rounded hover:bg-gray-800 group"
                >
                  <div className="text-sm font-semibold text-white group-hover:text-teal-400 transition-colors">{item.title}</div>
                  <div className="text-xs text-gray-400 mt-0.5 leading-snug">{item.description}</div>
                </Link>
              )
            )}
          </div>
          {viewAllLink && (
            <div className="col-span-2 pt-2 mt-1 border-t border-gray-700">
              <Link to={viewAllLink} className="block px-3 py-2 text-sm text-teal-400 hover:bg-gray-800 rounded font-medium">
                {viewAllLabel} →
              </Link>
            </div>
          )}
        </div>
        <div className="w-44 flex-shrink-0 bg-teal-700 p-5 flex flex-col justify-between">
          <div>
            <div className="text-sm font-bold text-white mb-2">{ctaTitle}</div>
            <p className="text-xs text-teal-100 leading-snug">{ctaText}</p>
          </div>
          <Link
            to={ctaLink}
            className="mt-4 inline-block w-full text-center text-xs font-semibold bg-white text-teal-800 px-3 py-2 rounded hover:bg-teal-50 transition-colors"
          >
            {ctaButtonLabel} →
          </Link>
        </div>
      </div>
    </div>
  );
};

interface SlimDropdownProps {
  items: { title: string; url: string; description: string }[];
  visible: boolean;
}

const SlimDropdown: React.FC<SlimDropdownProps> = ({ items, visible }) => (
  <div
    className="absolute left-0 top-full z-[200] w-72"
    style={{
      paddingTop: '8px',
      pointerEvents: visible ? 'auto' : 'none',
      opacity: visible ? 1 : 0,
      transform: visible ? 'translateY(0)' : 'translateY(-8px)',
      visibility: visible ? 'visible' : 'hidden',
      transition: 'opacity 180ms ease, transform 180ms ease, visibility 180ms',
    }}
  >
    <div className="bg-gray-900 rounded-lg shadow-2xl py-2">
      {items.map((item, i) => (
        <a
          key={i}
          href={item.url}
          target="_blank"
          rel="noopener noreferrer"
          className="block px-4 py-3 hover:bg-gray-800 group"
        >
          <div className="text-sm font-semibold text-white group-hover:text-teal-400 transition-colors">{item.title}</div>
          <div className="text-xs text-gray-400 mt-0.5">{item.description}</div>
        </a>
      ))}
    </div>
  </div>
);

type MenuName = 'solutions' | 'industries' | 'services' | 'caseStudies' | 'products' | null;

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeMenu, setActiveMenu] = useState<MenuName>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setActiveMenu(null);
    setIsOpen(false);
  }, [location.pathname]);

  const openMenu = (name: MenuName) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setActiveMenu(name);
  };

  const scheduleClose = () => {
    closeTimer.current = setTimeout(() => setActiveMenu(null), 120);
  };

  const isTransparent = location.pathname === '/' && !isScrolled;

  return (
    <header className={`fixed w-full z-50 transition-all duration-300 ${isTransparent ? 'bg-transparent' : 'bg-black'}`}>
      <nav className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">

          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img
              src="/agility hex fonts.png"
              alt="Agility.AI Technologies - AI Consulting and Data Analytics Solutions"
              className="h-16 md:h-20"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden xl:flex items-center space-x-5">

            {/* Solutions */}
            <div className="relative" onMouseEnter={() => openMenu('solutions')} onMouseLeave={scheduleClose}>
              <button className="flex items-center gap-1 text-base font-medium text-white hover:text-teal-400 transition-colors duration-200 whitespace-nowrap">
                Solutions
                <ChevronDown size={14} className={`transition-transform duration-200 ${activeMenu === 'solutions' ? 'rotate-180' : ''}`} />
              </button>
              <MegaDropdown
                items={solutions}
                visible={activeMenu === 'solutions'}
                ctaTitle="Ready to automate?"
                ctaText="Ship production-grade AI in weeks, not months."
                ctaLink="/contact"
                ctaButtonLabel="Get Started"
              />
            </div>

            {/* Industries */}
            <div className="relative" onMouseEnter={() => openMenu('industries')} onMouseLeave={scheduleClose}>
              <button className="flex items-center gap-1 text-base font-medium text-white hover:text-teal-400 transition-colors duration-200 whitespace-nowrap">
                Industries
                <ChevronDown size={14} className={`transition-transform duration-200 ${activeMenu === 'industries' ? 'rotate-180' : ''}`} />
              </button>
              <MegaDropdown
                items={industries}
                visible={activeMenu === 'industries'}
                ctaTitle="Your industry, our AI"
                ctaText="Tailored AI solutions built for your sector."
                ctaLink="/contact"
                ctaButtonLabel="Let's Talk"
              />
            </div>

            {/* Services */}
            <div className="relative" onMouseEnter={() => openMenu('services')} onMouseLeave={scheduleClose}>
              <button className="flex items-center gap-1 text-base font-medium text-white hover:text-teal-400 transition-colors duration-200 whitespace-nowrap">
                Services
                <ChevronDown size={14} className={`transition-transform duration-200 ${activeMenu === 'services' ? 'rotate-180' : ''}`} />
              </button>
              <MegaDropdown
                items={services.slice(0, 11)}
                visible={activeMenu === 'services'}
                ctaTitle="Hire top AI talent"
                ctaText="Senior ML engineers available in 48 hours."
                ctaLink="/hire-ai-developers"
                ctaButtonLabel="Hire Now"
                viewAllLink="/solutions"
                viewAllLabel="View All Services"
              />
            </div>

            {/* Case Studies */}
            <div className="relative" onMouseEnter={() => openMenu('caseStudies')} onMouseLeave={scheduleClose}>
              <button className="flex items-center gap-1 text-base font-medium text-white hover:text-teal-400 transition-colors duration-200 whitespace-nowrap">
                Case Studies
                <ChevronDown size={14} className={`transition-transform duration-200 ${activeMenu === 'caseStudies' ? 'rotate-180' : ''}`} />
              </button>
              <MegaDropdown
                items={caseStudies}
                visible={activeMenu === 'caseStudies'}
                ctaTitle="See real results"
                ctaText="Explore how we've delivered AI at scale."
                ctaLink="/case-studies"
                ctaButtonLabel="View All"
                viewAllLink="/case-studies"
                viewAllLabel="View All Case Studies"
                alignRight
              />
            </div>

            {/* Products */}
            <div className="relative" onMouseEnter={() => openMenu('products')} onMouseLeave={scheduleClose}>
              <button className="flex items-center gap-1 text-base font-medium text-white hover:text-teal-400 transition-colors duration-200 whitespace-nowrap">
                Products
                <ChevronDown size={14} className={`transition-transform duration-200 ${activeMenu === 'products' ? 'rotate-180' : ''}`} />
              </button>
              <SlimDropdown items={products} visible={activeMenu === 'products'} />
            </div>

            <Link to="/youtube" className="text-base font-medium text-white hover:text-teal-400 transition-colors duration-200 whitespace-nowrap">
              Videos
            </Link>

            <Link to="/about" className="text-base font-medium text-white hover:text-teal-400 transition-colors duration-200 whitespace-nowrap">
              About
            </Link>

            <Link to="/careers" className="text-base font-medium text-white hover:text-teal-400 transition-colors duration-200 whitespace-nowrap">
              Careers
            </Link>

            <Link to="/insights" className="text-base font-medium text-white hover:text-teal-400 transition-colors duration-200 whitespace-nowrap">
              Insights
            </Link>

            <Link
              to="/contact"
              className="text-base font-semibold bg-teal-500 text-white px-5 py-2 rounded-md hover:bg-teal-400 hover:scale-105 transition-all duration-200 whitespace-nowrap"
            >
              Contact Us
            </Link>
          </div>

          {/* Mobile hamburger */}
          <div className="xl:hidden">
            <button onClick={() => setIsOpen(!isOpen)} className="text-white p-1">
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="xl:hidden absolute top-full left-0 right-0 bg-gray-900 shadow-lg z-50 max-h-[80vh] overflow-y-auto">
            <div className="px-4 pt-2 pb-6 space-y-1">
              <div className="px-3 py-1 text-xs text-gray-500 uppercase tracking-wider">Solutions</div>
              {solutions.map((s) => (
                <Link key={s.path} to={s.path} className="block px-3 py-2 text-base text-white hover:text-teal-400 transition-colors" onClick={() => setIsOpen(false)}>
                  {s.title}
                </Link>
              ))}

              <div className="border-t border-gray-700 pt-2 mt-2">
                <div className="px-3 py-1 text-xs text-gray-500 uppercase tracking-wider">Industries</div>
                {industries.map((s) => (
                  <Link key={s.path} to={s.path} className="block px-3 py-2 text-base text-white hover:text-teal-400 transition-colors" onClick={() => setIsOpen(false)}>
                    {s.title}
                  </Link>
                ))}
              </div>

              <div className="border-t border-gray-700 pt-2 mt-2">
                <div className="px-3 py-1 text-xs text-gray-500 uppercase tracking-wider">Services</div>
                {services.map((s) => (
                  <Link key={s.path} to={s.path} className="block px-3 py-2 text-base text-white hover:text-teal-400 transition-colors" onClick={() => setIsOpen(false)}>
                    {s.title}
                  </Link>
                ))}
              </div>

              <div className="border-t border-gray-700 pt-2 mt-2">
                <div className="px-3 py-1 text-xs text-gray-500 uppercase tracking-wider">Case Studies</div>
                {caseStudies.map((s) => (
                  <Link key={s.path} to={s.path} className="block px-3 py-2 text-base text-white hover:text-teal-400 transition-colors" onClick={() => setIsOpen(false)}>
                    {s.title}
                  </Link>
                ))}
                <Link to="/case-studies" className="block px-3 py-2 text-base text-teal-400 hover:text-teal-300 transition-colors" onClick={() => setIsOpen(false)}>
                  All Case Studies →
                </Link>
              </div>

              <div className="border-t border-gray-700 pt-2 mt-2">
                <div className="px-3 py-1 text-xs text-gray-500 uppercase tracking-wider">Products</div>
                {products.map((s) => (
                  <a key={s.url} href={s.url} target="_blank" rel="noopener noreferrer" className="block px-3 py-2 text-base text-white hover:text-teal-400 transition-colors" onClick={() => setIsOpen(false)}>
                    {s.title}
                  </a>
                ))}
              </div>

              <div className="border-t border-gray-700 pt-2 mt-2 space-y-1">
                {[['Videos', '/youtube'], ['About', '/about'], ['Careers', '/careers'], ['Insights', '/insights']].map(([label, path]) => (
                  <Link key={path} to={path} className="block px-3 py-2 text-base text-white hover:text-teal-400 transition-colors" onClick={() => setIsOpen(false)}>
                    {label}
                  </Link>
                ))}
              </div>

              <div className="pt-3">
                <Link
                  to="/contact"
                  className="block w-full text-center text-base font-semibold bg-teal-500 text-white px-6 py-3 rounded-md hover:bg-teal-400 transition-colors"
                  onClick={() => setIsOpen(false)}
                >
                  Contact Us
                </Link>
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
